import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { InfoIcon } from "@icons";
import styles from "./Field.module.scss";
import {
    FieldControl,
    FieldDescription,
    FieldError,
    FieldLabel,
    FieldMessage,
    FieldRoot,
} from "./FieldParts";
import type {
    FieldControlAriaProps,
    FieldNameProps,
    FieldProps,
} from "./Field.types";

export type { FieldControlAriaProps, FieldNameProps };

/**
 * Copies only the naming props, for wrappers that hand a `Field`'s name on
 * from a larger props object without a rest spread.
 */
export const pickFieldNameProps = (props: FieldNameProps): FieldNameProps => {
    if (props["aria-labelledby"] !== undefined) {
        return { "aria-labelledby": props["aria-labelledby"] };
    }
    if (props.labelHidden === true) {
        return { label: props.label, labelHidden: true };
    }
    return { label: props.label, labelTooltip: props.labelTooltip };
};

/**
 * Projects the id and aria props from a `Field.Control render` callback onto
 * the shape custom controls forward to their focusable node.
 */
export const pickFieldControlAriaProps = ({
    id,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
}: FieldControlAriaProps): FieldControlAriaProps => ({
    id,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
});

const FieldComposed = ({
    label,
    labelHidden = false,
    "aria-labelledby": ariaLabelledby,
    control = "input",
    optional = false,
    labelTooltip,
    description,
    error,
    invalid,
    children,
    className,
    style,
    ref,
}: FieldProps) => {
    const hasError = error !== undefined && error !== null;
    const isInvalid = invalid ?? hasError;
    const labelContent = (
        <>
            <span>{label}</span>
            {optional && <span className={styles.optional}>(optional)</span>}
        </>
    );

    return (
        <FieldRoot
            ref={ref}
            invalid={isInvalid}
            className={className}
            style={style}
        >
            {ariaLabelledby === undefined && (
                <div
                    className={classNames(
                        styles.labelRow,
                        labelHidden && styles.labelRowHidden,
                    )}
                >
                    {control === "button" ? (
                        <FieldLabel nativeLabel={false} render={<span />}>
                            {labelContent}
                        </FieldLabel>
                    ) : (
                        <FieldLabel>{labelContent}</FieldLabel>
                    )}
                    {labelTooltip !== undefined && labelTooltip !== null && (
                        <Tooltip content={labelTooltip}>
                            <button
                                type="button"
                                aria-label="More information"
                                className={styles.labelTooltip}
                            >
                                <InfoIcon aria-hidden />
                            </button>
                        </Tooltip>
                    )}
                </div>
            )}
            {children}
            <FieldMessage error={error} description={description} />
        </FieldRoot>
    );
};

export const Field = Object.assign(FieldComposed, {
    Root: FieldRoot,
    Label: FieldLabel,
    Description: FieldDescription,
    Error: FieldError,
    Control: FieldControl,
    Message: FieldMessage,
});
